import React, { useState, useEffect } from 'react';
import Icon from './Icon';

/**
 * Development-only component for quick user switching
 * Displays a dropdown with all available users for easy testing
 * Uses dev-only endpoints that don't require passwords
 */
function DevLoginDropdown({ onLogin, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const [fetchingUsers, setFetchingUsers] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'super_admin', 'company_admin', 'technician', 'user'
  const buildApiUrl = (pathname) => {
    if (!pathname) return '/';
    return pathname.startsWith('/') ? pathname : `/${pathname}`;
  };

  // Fetch users from the database on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Refetch users when modal is expanded (in case previous fetch failed)
  useEffect(() => {
    if (expanded) {
      fetchUsers();
    }
  }, [expanded]);

  const fetchUsers = async () => {
    setFetchingUsers(true);
    setError(''); // Clear any previous errors
    try {
      const response = await fetch(buildApiUrl('/api/auth/dev/users'), {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.users && Array.isArray(data.users)) {
          setUsers(data.users);
          setError(''); // Clear error on success
        } else {
          console.error('Invalid response format:', data);
          setError('Invalid response from server');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || errorData.message || `Server returned ${response.status}`;
        console.error('Dev users endpoint error:', errorMsg, errorData);
        setError(`Failed to load users: ${errorMsg}`);
      }
    } catch (err) {
      console.error('Dev users endpoint error:', err.message);
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setFetchingUsers(false);
    }
  };

  const handleQuickLogin = async (username) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(buildApiUrl('/api/auth/dev/quick-login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ username })
      });
      
      const data = await response.json();
      
      if (response.ok && data.user) {
        onLogin(data.user, data.requires_setup);
        setExpanded(false);
        setError('');
        // Force page reload to ensure all components get fresh data
        window.location.reload();
      } else {
        setError(data.error || 'Login failed');
        // If user not found, show available users
        if (data.available_users) {
          console.log('Available users:', data.available_users);
        }
      }
    } catch (err) {
      setError(`Login failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Show in development OR on localhost/herokuapp.com (for testing)
  // To hide completely in production, remove 'herokuapp.com' from this check
  const isDevOrTest = 
    process.env.NODE_ENV !== 'production' || 
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    window.location.hostname.includes('herokuapp.com');
  
  if (!isDevOrTest) {
    return null;
  }

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return 'admin';
      case 'company_admin': return 'companies';
      case 'technician': return 'technician';
      default: return 'profile';
    }
  };

  // Filter and format users for display
  const filteredUsers = roleFilter === 'all' 
    ? users 
    : users.filter(u => u.role === roleFilter);
  
  const displayUsers = filteredUsers.map(u => {
    const displayName = u.firstname && u.surname 
      ? `${u.firstname} ${u.surname}` 
      : u.username;
    const companyInfo = u.company_name ? ` - ${u.company_name}` : '';
    return {
      username: u.username,
      role: u.role,
      label: displayName + companyInfo,
      iconName: getRoleIcon(u.role),
      sublabel: u.company_name || u.role,
      companyId: u.company_id,
      companyName: u.company_name
    };
  });
  
  // Count users by role
  const roleCounts = {
    all: users.length,
    super_admin: users.filter(u => u.role === 'super_admin').length,
    company_admin: users.filter(u => u.role === 'company_admin').length,
    technician: users.filter(u => u.role === 'technician').length,
    user: users.filter(u => u.role === 'user').length
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9999,
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Toggle Button */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: expanded ? '#dc3545' : '#28a745',
          color: '#ffffff',
          border: 'none',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          fontSize: '20px',
          cursor: 'pointer',
          boxShadow: 'var(--shadow-md)',
          transition: 'all 0.2s ease'
        }}
        title="Dev Quick Login"
      >
        {expanded ? 'x' : '+'}
      </button>

      {/* Dropdown Panel */}
      {expanded && (
        <div style={{
          position: 'absolute',
          bottom: '60px',
          right: '0',
          background: 'white',
          borderRadius: '12px',
          boxShadow: 'var(--shadow-lg)',
          padding: '16px',
          minWidth: '280px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          <div style={{
            fontWeight: 'bold',
            fontSize: '14px',
            marginBottom: '12px',
            color: '#333',
            borderBottom: '1px solid #eee',
            paddingBottom: '8px'
          }}>
            <Icon name="rocket" color="warning" darkMode={false} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            DEV Quick Login
          </div>

          {currentUser && (
            <div style={{
              background: '#e7f3ff',
              padding: '8px 12px',
              borderRadius: '6px',
              marginBottom: '12px',
              fontSize: '12px'
            }}>
              <strong>Currently logged in as:</strong><br />
              {currentUser.firstname && currentUser.surname 
                ? `${currentUser.firstname} ${currentUser.surname}` 
                : currentUser.username}
              {currentUser.company_name && <><br /><Icon name="companies" color="info" darkMode={false} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> {currentUser.company_name}</>}
              <br />
              <span style={{ color: 'var(--color-gray)', fontSize: '11px' }}>{currentUser.role}</span>
            </div>
          )}

          {error && (
            <div style={{
              background: '#fee',
              color: '#c00',
              padding: '8px',
              borderRadius: '4px',
              fontSize: '12px',
              marginBottom: '8px'
            }}>
              {error}
            </div>
          )}

          {/* Role Filter Buttons */}
          <div style={{
            display: 'flex',
            gap: '4px',
            marginBottom: '12px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setRoleFilter('all')}
              style={{
                padding: '6px 10px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                cursor: 'pointer',
                background: roleFilter === 'all' ? 'var(--lime-primary)' : '#e2e8f0',
                color: roleFilter === 'all' ? '#0a0a1a' : '#4a5568',
                fontWeight: roleFilter === 'all' ? 'bold' : 'normal'
              }}
            >
              All ({roleCounts.all})
            </button>
            <button
              onClick={() => setRoleFilter('super_admin')}
              style={{
                padding: '6px 10px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                cursor: 'pointer',
                background: roleFilter === 'super_admin' ? '#f6ad55' : '#e2e8f0',
                color: roleFilter === 'super_admin' ? 'white' : '#4a5568',
                fontWeight: roleFilter === 'super_admin' ? 'bold' : 'normal'
              }}
            >
              <Icon name="admin" color="warning" darkMode={false} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Super ({roleCounts.super_admin})
            </button>
            <button
              onClick={() => setRoleFilter('company_admin')}
              style={{
                padding: '6px 10px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                cursor: 'pointer',
                background: roleFilter === 'company_admin' ? '#48bb78' : '#e2e8f0',
                color: roleFilter === 'company_admin' ? 'white' : '#4a5568',
                fontWeight: roleFilter === 'company_admin' ? 'bold' : 'normal'
              }}
            >
              <Icon name="companies" color="success" darkMode={false} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Company ({roleCounts.company_admin})
            </button>
            <button
              onClick={() => setRoleFilter('technician')}
              style={{
                padding: '6px 10px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                cursor: 'pointer',
                background: roleFilter === 'technician' ? '#9f7aea' : '#e2e8f0',
                color: roleFilter === 'technician' ? 'white' : '#4a5568',
                fontWeight: roleFilter === 'technician' ? 'bold' : 'normal'
              }}
            >
              <Icon name="technician" color="info" darkMode={false} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Tech ({roleCounts.technician})
            </button>
            <button
              onClick={() => setRoleFilter('user')}
              style={{
                padding: '6px 10px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '11px',
                cursor: 'pointer',
                background: roleFilter === 'user' ? '#4299e1' : '#e2e8f0',
                color: roleFilter === 'user' ? 'white' : '#4a5568',
                fontWeight: roleFilter === 'user' ? 'bold' : 'normal'
              }}
            >
              User ({roleCounts.user})
            </button>
          </div>

          {fetchingUsers ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-gray)' }}>
              Loading users...
            </div>
          ) : displayUsers.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-gray)' }}>
              No users found
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto' }}>
              {displayUsers.map((user, index) => (
                <button
                  key={`${user.username}-${index}`}
                  onClick={() => handleQuickLogin(user.username)}
                  disabled={loading || (currentUser && currentUser.username === user.username)}
                  style={{
                    padding: '10px 12px',
                    border: currentUser && currentUser.username === user.username 
                      ? '2px solid #28a745' 
                      : '1px solid #ddd',
                    borderRadius: '8px',
                    background: currentUser && currentUser.username === user.username
                      ? '#e8f5e9'
                      : '#fff',
                    cursor: loading ? 'wait' : 'pointer',
                    textAlign: 'left',
                    fontSize: '13px',
                    transition: 'all 0.15s ease',
                    opacity: loading ? 0.6 : 1
                  }}
                  onMouseOver={(e) => {
                    if (!(currentUser && currentUser.username === user.username)) {
                      e.target.style.background = '#f5f5f5';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!(currentUser && currentUser.username === user.username)) {
                      e.target.style.background = '#fff';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name={user.iconName} color="secondary" darkMode={false} size={18} />
                    {user.label}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--color-gray-light)', marginTop: '2px' }}>{user.sublabel}</div>
                  {currentUser && currentUser.username === user.username && (
                    <span style={{ position: 'absolute', right: '12px' }}>
                      <Icon name="success" color="success" darkMode={false} size={18} />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #eee',
            fontSize: '11px',
            color: 'var(--color-gray-light)'
          }}>
            Click any user to switch instantly (no password needed)
          </div>
        </div>
      )}
    </div>
  );
}

export default DevLoginDropdown;


